import { forceAddonsMinMaxConstraints } from '@proton/components/containers/payments/planCustomizer';
import {
    type ADDON_NAMES,
    AddonLimit,
    CURRENCIES,
    type Currency,
    FREE_PLAN,
    MAX_DOMAIN_PRO_ADDON,
    MAX_IPS_ADDON,
    MAX_MEMBER_ADDON,
    PLANS,
    type Plan,
    type PlansMap,
    fixPlanName,
    getPlanByName,
    getPlanMaxIPs,
    getSupportedAddons,
    getValidCycle,
    isDomainAddon,
    isIpAddon,
    isMemberAddon,
    planToPlanIDs,
} from '@proton/payments';
import type { OtherProductParam, ProductParam } from '@proton/shared/lib/apps/product';
import { otherProductParamValues } from '@proton/shared/lib/apps/product';
import type { APP_NAMES } from '@proton/shared/lib/constants';
import { APPS } from '@proton/shared/lib/constants';
import { getCookie } from '@proton/shared/lib/helpers/cookies';
import clamp from '@proton/utils/clamp';

import type { PlanParameters, SignupDefaults } from '../single-signup-v2/interface';

export const SERVICES: { [key: string]: APP_NAMES } = {
    mail: APPS.PROTONMAIL,
    calendar: APPS.PROTONCALENDAR,
    drive: APPS.PROTONDRIVE,
    vpn: APPS.PROTONVPN_SETTINGS,
    pass: APPS.PROTONPASS,
    docs: APPS.PROTONDOCS,
    wallet: APPS.PROTONWALLET,
    lumo: APPS.PROTONLUMO,
    authenticator: APPS.PROTONAUTHENTICATOR,
    meet: APPS.PROTONMEET,
};

const getProduct = (maybeProduct: string | undefined): APP_NAMES | undefined => {
    if (!maybeProduct) {
        return;
    }
    const directMatch = SERVICES[maybeProduct];
    if (directMatch) {
        return directMatch;
    }
    const partialMatch = Object.keys(SERVICES).find((service) => maybeProduct.startsWith(service));
    if (partialMatch) {
        return SERVICES[partialMatch];
    }
};

const productParams = new Set(otherProductParamValues);

const getSanitisedProductParam = (value: string | undefined): OtherProductParam | undefined => {
    if (productParams.has(value as any)) {
        return value as OtherProductParam;
    }
};

const getProductParam = (
    product: APP_NAMES | undefined,
    productParam: string | undefined
): ProductParam | undefined => {
    const sanitisedProductParam = getSanitisedProductParam(productParam);
    return product || sanitisedProductParam;
};

export interface ProductParams {
    product: APP_NAMES | undefined;
    productParam: ProductParam;
}

export const getProductParams = (pathname: string, searchParams: URLSearchParams): ProductParams => {
    const maybeProductPathname = pathname.match(/\/([^/]*)/)?.[1];
    const maybeProductParam = (
        searchParams.get('service') ||
        searchParams.get('product') ||
        searchParams.get('app') ||
        undefined
    )
        ?.toLowerCase()
        ?.replace('proton-', '');

    let [product] = [maybeProductPathname, maybeProductParam].map((value) => getProduct(value)).filter(Boolean);
    let [productParam] = [maybeProductPathname, maybeProductParam]
        .map((value) => getProductParam(product, value))
        .filter(Boolean);

    productParam = productParam || 'generic';
    const planParam = searchParams.get('plan') as any;

    if (productParam === 'business') {
        if ([PLANS.MAIL_PRO, PLANS.MAIL_BUSINESS].includes(planParam)) {
            product = APPS.PROTONMAIL;
            productParam = APPS.PROTONMAIL;
        }
    }

    if (!product) {
        const plans = [
            PLANS.MAIL_PRO,
            PLANS.MAIL_BUSINESS,
            PLANS.BUNDLE_PRO,
            PLANS.BUNDLE_PRO_2024,
            PLANS.BUNDLE_BIZ_2025,
        ];
        if (plans.includes(planParam)) {
            product = APPS.PROTONMAIL;
        }
    }

    return { product, productParam };
};

export const getSignupSearchParams = (
    pathname: string,
    searchParams: URLSearchParams,
    defaults?: Partial<SignupDefaults>
) => {
    const maybeCurrency = searchParams.get('currency')?.toUpperCase() as Currency | undefined;
    const currency = maybeCurrency && CURRENCIES.includes(maybeCurrency) ? maybeCurrency : undefined;

    const maybeCycle = Number(searchParams.get('billing')) || Number(searchParams.get('cycle'));
    const cycle = getValidCycle(maybeCycle);

    const maybeMinimumCycle = Number(searchParams.get('minimumCycle'));
    const minimumCycle = getValidCycle(maybeMinimumCycle);

    const maybeUsers = Number(searchParams.get('users'));
    const users = maybeUsers >= 1 && maybeUsers <= MAX_MEMBER_ADDON ? maybeUsers : undefined;
    const maybeDomains = Number(searchParams.get('domains'));
    const domains = maybeDomains >= 1 && maybeDomains <= MAX_DOMAIN_PRO_ADDON ? maybeDomains : undefined;
    const maybeIps = Number(searchParams.get('ips'));
    const ips = maybeIps >= 1 && maybeIps <= MAX_IPS_ADDON ? maybeIps : undefined;

    const { product } = getProductParams(pathname, searchParams);

    // plan is validated by comparing plans after it's loaded
    const maybePreSelectedPlan = searchParams.get('plan');
    const maybeDefaultPlan = searchParams.get('defaultplan');

    const referrer = searchParams.get('referrer') || undefined; // referral ID
    const invite = searchParams.get('invite') || undefined;
    const coupon = searchParams.get('coupon')?.toUpperCase() || undefined;
    const type = searchParams.get('type') || undefined;
    const maybeHfp = searchParams.get('hfp');
    const hideFreePlan = ['true', '1'].includes(maybeHfp as any) || getCookie('offer') === 'bestdeal' || false;
    const email = searchParams.get('email') || undefined;
    const orgName = searchParams.get('orgName') || undefined;
    const source = searchParams.get('source') || undefined;
    const noPromo = searchParams.get('noPromo');
    const trial = searchParams.get('trial') === 'true';

    return {
        email,
        coupon,
        currency,
        cycle: cycle || defaults?.cycle,
        minimumCycle,
        preSelectedPlan: fixPlanName(maybePreSelectedPlan || defaults?.plan, 'GetSignupSearchParams'),
        defaultPlan: fixPlanName(maybeDefaultPlan || defaults?.plan, 'GetSignupSearchParams'),
        product,
        users,
        noPromo: noPromo !== null && noPromo !== 'false' && noPromo !== '0',
        domains,
        ips,
        referrer,
        invite,
        type,
        hideFreePlan,
        orgName,
        source,
        trial,
    };
};

export const getPlanIDsFromParams = (
    plans: Plan[],
    currency: Currency,
    signupParameters: {
        preSelectedPlan?: string;
        domains?: number;
        ips?: number;
        users?: number;
    },
    defaults: Pick<SignupDefaults, 'plan' | 'addons'>
): PlanParameters => {
    const freePlanIDs = {};

    const defaultPlan =
        defaults.plan === FREE_PLAN.Name
            ? FREE_PLAN
            : (getPlanByName(plans, defaults.plan, currency, undefined, false) ?? FREE_PLAN);
    const defaultPlanIDs = { ...planToPlanIDs(defaultPlan), ...defaults.addons };

    const defaultResponse = {
        planIDs: defaultPlanIDs,
        plan: defaultPlan,
        defined: false,
    };

    if (!signupParameters.preSelectedPlan) {
        return defaultResponse;
    }

    if (signupParameters.preSelectedPlan === 'free') {
        return { planIDs: freePlanIDs, defined: true, plan: FREE_PLAN };
    }

    const plan = getPlanByName(plans, signupParameters.preSelectedPlan, currency, undefined, false);

    if (!plan) {
        return defaultResponse;
    }

    const preSelectedPlanIDs = { [plan.Name]: 1 };
    const planIDs =
        // if the default plan has addons and if user selected the default plan via URL parameters, then we want to
        // automatically transfer the default addons if there are any
        plan.Name === defaultPlan.Name
            ? {
                  ...preSelectedPlanIDs,
                  ...defaults.addons,
              }
            : preSelectedPlanIDs;

    const supportedAddons = getSupportedAddons(planIDs);

    if (signupParameters.users !== undefined) {
        const usersAddon = plans.find(
            ({ Name }) => isMemberAddon(Name) && supportedAddons[Name as keyof typeof supportedAddons]
        );

        const clampedUsers = clamp(
            signupParameters.users,
            0,
            usersAddon ? AddonLimit[usersAddon.Name as ADDON_NAMES] : 0
        );
        const amount = clampedUsers - plan.MaxMembers;
        if (usersAddon && amount > 0) {
            planIDs[usersAddon.Name] = amount;
        }
    }

    if (signupParameters.domains !== undefined) {
        const domainsAddon = plans.find(
            ({ Name }) => isDomainAddon(Name) && supportedAddons[Name as keyof typeof supportedAddons]
        );
        const amount = signupParameters.domains - plan.MaxDomains;
        if (domainsAddon && amount > 0) {
            planIDs[domainsAddon.Name] = amount;
        }
    }

    if (signupParameters.ips !== undefined) {
        const ipsAddon = plans.find(
            ({ Name }) => isIpAddon(Name) && supportedAddons[Name as keyof typeof supportedAddons]
        );
        const amount = signupParameters.ips - (getPlanMaxIPs(plan) + (ipsAddon?.Quantity || 0));
        if (ipsAddon && amount > 0) {
            planIDs[ipsAddon.Name] = amount;
        }
    }

    return { planIDs, defined: true, plan };
};

export const getPlanIDsFromParamsWithForcedAddons = ({
    plans,
    currency,
    signupParameters,
    defaults,
    plansMap,
}: {
    plans: Plan[];
    currency: Currency;
    signupParameters: {
        preSelectedPlan?: string;
        domains?: number;
        ips?: number;
        users?: number;
    };
    defaults: Pick<SignupDefaults, 'plan' | 'addons'>;
    plansMap: PlansMap;
}) => {
    const result = getPlanIDsFromParams(plans, currency, signupParameters, defaults);
    const planIDs =
        forceAddonsMinMaxConstraints({
            selectedPlanIDs: result.planIDs,
            plansMap,
            currency,
            subscription: undefined,
        }) ?? result.planIDs;
    return { ...result, planIDs };
};
